<template>
  <b-container fluid>
    <backlink />
    <h1>Transferts inter-entrepôts</h1>
    <p class="text-muted">
      Mouvements de stock entre deux entrepôts internes (à ne pas confondre avec les transferts clients).
    </p>

    <AdminTable :config="config" :options="options" :items_provider="itemsProvider">
      <template slot="status" slot-scope="{ data }">
        <b-badge :variant="statusVariant(data.item.status_slug)">{{ data.value }}</b-badge>
      </template>
      <template slot="actions" slot-scope="{ data }">
        <template v-if="isSent(data.item)">
          <b-button
            v-if="$root.isAuthorized('warehouse-transfer_edit')"
            @click="receive(data.item)"
            v-b-tooltip.hover
            title="Réceptionner"
            variant="outline-success"
          >
            <i class="far fa-check"></i>
          </b-button>
          <b-button
            v-if="$root.isAuthorized('warehouse-transfer_delete')"
            @click="cancel(data.item)"
            v-b-tooltip.hover
            title="Annuler"
            variant="outline-secondary"
          >
            <i class="far fa-times"></i>
          </b-button>
        </template>
      </template>
    </AdminTable>
  </b-container>
</template>

<script>
import AdminTable from "@/components/Global/AdminTableSync";
import config from "./config";

const STATUS_SENT = "warehouse-transfer-envoye";
const STATUS_PARTIALLY_RECEIVED = "warehouse-transfer-receptionne-partiellement";
const STATUS_RECEIVED = "warehouse-transfer-receptionne";

export default {
  name: "WarehouseTransferList",
  components: {
    AdminTable,
  },
  data() {
    return {
      config,
      // Produits et références déjà transférés (options du filtre « Produit / référence »).
      transferredItems: [],
    };
  },
  computed: {
    options() {
      const warehouses = (this.$store.getters["warehouse/getAll"] || []).map((w) => ({
        value: w.id,
        text: w.internal_code ? w.internal_code + " - " + w.name : w.name,
      }));
      return {
        // Onglets, dans l'ordre du workflow.
        status_options: [
          { value: STATUS_SENT, text: "Envoyé" },
          { value: STATUS_PARTIALLY_RECEIVED, text: "Réceptionné partiellement" },
          { value: STATUS_RECEIVED, text: "Réceptionné" },
          { value: "warehouse-transfer-annule", text: "Annulé" },
        ],
        content_options: [
          { value: "product", text: "Produits (vente)" },
          { value: "reference", text: "Références uniques" },
        ],
        source_warehouse_options: warehouses,
        target_warehouse_options: warehouses,
        item_options: this.transferredItems.map((item) => ({
          value: item.type + "-" + item.id,
          text:
            (item.type === "reference" ? "Référence " : "Produit ") +
            [item.internal_code, item.name].filter(Boolean).join(" - "),
        })),
      };
    },
  },
  mounted() {
    this.$store.dispatch("warehouse/getAll", true);
    this.$http.get(process.env.BACK_API_URL + "warehouse-transfer/items").then((response) => {
      this.transferredItems = (response.data && response.data.items) || [];
    });
  },
  methods: {
    /**
     * Fournit les données à AdminTableSync, appelée à chaque rafraîchissement de la liste.
     * @param   {object}  ctx   Contexte envoyé par b-table
     * @returns {Promise}       { total, page, items }
     */
    itemsProvider(ctx) {
      const filters = ctx.extended_filters_model;
      const day = (date) => (date ? this.$moment(date).format("YYYY-MM-DD") : null);
      return this.$http
        .get(process.env.BACK_API_URL + "warehouse-transfer", {
          params: {
            page: ctx.currentPage,
            limit: ctx.perPage,
            sortBy: ctx.sortBy,
            sortDesc: ctx.sortDesc ? "DESC" : "ASC",
            search: ctx.filter,
            status: filters.status,
            source: filters.source_warehouse,
            target: filters.target_warehouse,
            content: filters.content,
            item: filters.item,
            start_date: day(filters.period && filters.period.start),
            end_date: day(filters.period && filters.period.end),
          },
        })
        .then((response) => ({
          total: response.data.total,
          page: parseInt(response.data.page),
          // Le slug est mis de côté : la colonne Statut affiche le libellé, le badge
          // et les actions dépendent du slug.
          items: (response.data.transfers || []).map((t) => ({
            ...t,
            status_slug: t.status ? t.status.slug : null,
          })),
        }))
        .catch(() => {
          this.$toastr.error("Le chargement de la liste a échoué");
          return { total: 0, page: 1, items: [] };
        });
    },
    // Un transfert partiellement réceptionné reste actionnable sur son reliquat.
    isSent(item) {
      return [STATUS_SENT, STATUS_PARTIALLY_RECEIVED].indexOf(item.status_slug) >= 0;
    },
    statusVariant(slug) {
      if (slug === STATUS_SENT) return "warning";
      if (slug === STATUS_PARTIALLY_RECEIVED) return "info";
      if (slug === STATUS_RECEIVED) return "success";
      return "secondary";
    },
    receive(item) {
      this.$http
        .patch(process.env.BACK_API_URL + "warehouse-transfer/" + item.id + "/receive")
        .then(() => {
          this.$toastr.success("Transfert " + item.transfer_number + " réceptionné.");
          this.$root.$emit("refresh");
        })
        .catch((error) => {
          this.$toastr.error((error.response && error.response.data && error.response.data.message) || "Échec de la réception.");
        });
    },
    cancel(item) {
      this.$http
        .patch(process.env.BACK_API_URL + "warehouse-transfer/" + item.id + "/cancel")
        .then(() => {
          this.$toastr.success("Transfert " + item.transfer_number + " annulé.");
          this.$root.$emit("refresh");
        })
        .catch((error) => {
          this.$toastr.error((error.response && error.response.data && error.response.data.message) || "Échec de l'annulation.");
        });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/WarehouseTransfer/WarehouseTransferList.vue